export interface AthleteAttributes {
  pace: number; // 0 - 100
  technical: number;
  tactical: number;
  physical: number;
  mental: number;
  finishing: number;
}

interface AthleteRadarChartProps {
  attributes?: AthleteAttributes;
  size?: number;
}

const DEFAULT_ATTRIBUTES: AthleteAttributes = {
  pace: 88,
  technical: 85,
  tactical: 82,
  physical: 86,
  mental: 84,
  finishing: 80,
};

export function AthleteRadarChart({ attributes = DEFAULT_ATTRIBUTES, size = 260 }: AthleteRadarChartProps) {
  const center = size / 2;
  const radius = (size / 2) * 0.72;

  const labels = [
    { key: "pace", label: "Pace", val: attributes.pace },
    { key: "technical", label: "Technical", val: attributes.technical },
    { key: "tactical", label: "Tactical", val: attributes.tactical },
    { key: "physical", label: "Physical", val: attributes.physical },
    { key: "mental", label: "Mental", val: attributes.mental },
    { key: "finishing", label: "Finishing", val: attributes.finishing },
  ];

  const totalPoints = labels.length;
  const angleStep = (Math.PI * 2) / totalPoints;

  // Grid level polygons (20%, 40%, 60%, 80%, 100%)
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];

  const getCoordinates = (index: number, valueRatio: number) => {
    const angle = index * angleStep - Math.PI / 2;
    const r = radius * valueRatio;
    const x = center + r * Math.cos(angle);
    const y = center + r * Math.sin(angle);
    return { x, y };
  };

  // Generate data polygon points
  const dataPolygonPoints = labels
    .map((item, index) => {
      const ratio = Math.max(0.1, Math.min(1.0, item.val / 100));
      const { x, y } = getCoordinates(index, ratio);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  return (
    <div className="relative flex flex-col items-center justify-center p-2">
      <svg width={size} height={size} className="overflow-visible">
        {/* Background Grid Rings */}
        {gridLevels.map((lvl) => {
          const points = Array.from({ length: totalPoints })
            .map((_, i) => {
              const { x, y } = getCoordinates(i, lvl);
              return `${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(" ");

          return (
            <polygon
              key={lvl}
              points={points}
              fill="none"
              stroke="rgba(255, 255, 255, 0.08)"
              strokeWidth="1"
            />
          );
        })}

        {/* Axis Spokes */}
        {labels.map((_, index) => {
          const { x, y } = getCoordinates(index, 1.0);
          return (
            <line
              key={index}
              x1={center}
              y1={center}
              x2={x}
              y2={y}
              stroke="rgba(255, 255, 255, 0.12)"
              strokeWidth="1"
            />
          );
        })}

        {/* Data Shape Fill */}
        <polygon
          points={dataPolygonPoints}
          fill="rgba(234, 179, 8, 0.25)"
          stroke="#EAB308"
          strokeWidth="2.5"
          strokeLinejoin="round"
          className="transition-all duration-500 ease-out"
        />

        {/* Data Vertices */}
        {labels.map((item, index) => {
          const ratio = Math.max(0.1, Math.min(1.0, item.val / 100));
          const { x, y } = getCoordinates(index, ratio);
          return (
            <circle
              key={item.key}
              cx={x}
              cy={y}
              r="4"
              fill="#FACC15"
              stroke="#000"
              strokeWidth="1.5"
            />
          );
        })}

        {/* Labels & Numeric Scores */}
        {labels.map((item, index) => {
          const { x, y } = getCoordinates(index, 1.22);
          return (
            <g key={item.key} transform={`translate(${x}, ${y})`}>
              <text
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-slate-300 text-[10px] font-bold uppercase tracking-wider"
              >
                {item.label}
              </text>
              <text
                y="11"
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-yellow-400 text-[11px] font-black font-mono"
              >
                {item.val}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
